import { useGetProFormasByDeal } from 'queries/ProForma/useGetProFormasByDeal';
import { useGetProjectBudgetScenarios } from 'queries/BudgetScenario/useGetProjectBudgetScenarios';
import useGetProFormaRoom from 'queries/ProForma/useGetProFormaRoom';
import { useAuthContext } from 'contexts/AuthContext';

interface IUseProjectActualsForDealParams {
    dealId: number;
    enabled?: boolean;
}

export const useProjectActualsForDeal = ({ dealId, enabled = true }: IUseProjectActualsForDealParams) => {
    const { selectedOrganizationId } = useAuthContext();

    const { data: proformas } = useGetProFormasByDeal({
        dealId,
    }, {
        enabled,
        staleTime: Infinity,
        gcTime: 1000 * 60 * 60,
        refetchOnWindowFocus: false,
    });

    const activeProformaWithProject = proformas?.find(pf => pf.isActive && pf.subprojectId);

    const { data: budgetScenariosData, isLoading: isLoadingBudgetScenarios } = useGetProjectBudgetScenarios(
        activeProformaWithProject?.subprojectId?.toString() || '',
        {
            enabled: enabled && !!activeProformaWithProject?.subprojectId,
            staleTime: Infinity,
            gcTime: 1000 * 60 * 60,
            refetchOnWindowFocus: false,
        },
    );

    let scenarioId: string | null = null;
    if (activeProformaWithProject && budgetScenariosData?.isEarlyPlanning && budgetScenariosData?.budgetScenarios?.length) {
        const persistedScenarioId = activeProformaWithProject.projectBudgetScenarioId;
        const persistedScenarioExists = persistedScenarioId && budgetScenariosData.budgetScenarios.some(
            scenario => scenario.correlationId === persistedScenarioId,
        );

        scenarioId = persistedScenarioExists
            ? persistedScenarioId
            : budgetScenariosData.budgetScenarios[0]?.correlationId ?? null;
    }

    const proFormaRoomQuery = useGetProFormaRoom(
        dealId,
        activeProformaWithProject?.id || 0,
        selectedOrganizationId!,
        true, // shouldGetActuals = true
        'Total Anticipated Cost', // forecastMetric
        scenarioId,
        {
            enabled: enabled && !!activeProformaWithProject && !isLoadingBudgetScenarios,
            staleTime: Infinity,
            gcTime: 1000 * 60 * 60,
            refetchOnWindowFocus: false,
        },
    );

    if (!activeProformaWithProject) {
        return {
            data: null,
            isLoading: false,
            error: null,
            refetch: () => {},
        };
    }

    return proFormaRoomQuery;
};
